import React, { useEffect, useId, useRef } from 'react';
import {
  Box,
  Button,
  ButtonProps,
  Dialog,
  DialogProps,
  DialogActions,
  DialogContent,
  IconButton,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import infoAnimation from '../../lottie/info.json';
import Close from '@mui/icons-material/Close';
import { ResponsiveStyleValue } from '@mui/system';
import ModalActionGroup from './ModalActionGroup';
import { actionButtonSizingSx } from './buttonSizing';
import { LOTTIE_SIZES, lottieFrameStyle, lottieContentStyle, lottieRendererSettings } from './lottieSizing';

export interface AppDialogProps {
  id?: string;
  open: boolean;
  onClose: () => void;
  onEntered?: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  animationData?: React.ComponentProps<typeof Lottie>['animationData'];
  animationSpeed?: number;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: ResponsiveStyleValue<number | string>;
  container?: DialogProps['container'];
  showCloseButton?: boolean;
  closeLabel?: string;
  contentSx?: SxProps<Theme>;
  className?: string;
  mobileSheet?: boolean;
}

/** Shared shell for product dialogs. Content and actions change; framing does not. */
export default function AppDialog({
  id, open, onClose, onEntered, title, description, animationData = infoAnimation, animationSpeed = 1, children, actions,
  maxWidth = 480, container, showCloseButton = true, closeLabel = 'Close dialog', contentSx, className, mobileSheet = false,
}: AppDialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const smallScreen = useMediaQuery(useTheme().breakpoints.down('md'));
  const sheet = mobileSheet && smallScreen;
  useEffect(() => {
    lottieRef.current?.setSpeed(animationSpeed);
    if (reducedMotion) lottieRef.current?.goToAndStop(0, true);
    else if (open) lottieRef.current?.play();
    else lottieRef.current?.pause();
  }, [animationSpeed, open, reducedMotion, animationData]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      className={className}
      container={container}
      maxWidth={false}
      fullWidth
      sx={sheet ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
      slotProps={{
        transition: { onEntered },
        paper: {
          id,
          sx: {
            background: '#181B24',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '20px',
            boxSizing: 'border-box',
            width: 'calc(100% - 32px)',
            maxWidth,
            maxHeight: 'calc(100dvh - 32px)',
            overflowX: 'hidden',
            m: 2,
            boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
            ...(sheet ? { width: '100%', maxWidth: '100%', maxHeight: '85dvh', m: 0, borderRadius: '24px 24px 0 0', pb: 'env(safe-area-inset-bottom)' } : {}),
          },
        },
        backdrop: { sx: { backgroundColor: 'rgba(5,8,14,0.82)' } },
      }}
    >
      {sheet && <Box aria-hidden="true" sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#B9BAC5', mx: 'auto', mt: 1.5, flexShrink: 0 }} />}
      <Box component="header" sx={{
        display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 },
        flexShrink: 0, px: { xs: 3, sm: 3.5 }, py: 2.5,
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}>
        <Box aria-hidden="true" sx={{
          ...lottieFrameStyle(LOTTIE_SIZES.dialog),
          alignSelf: 'center', placeItems: 'center',
          '& > *': { minWidth: 0, minHeight: 0, maxWidth: '100%', maxHeight: '100%' },
          '@media (max-height: 520px)': { display: 'none' },
        }}><Lottie
          lottieRef={lottieRef}
          animationData={animationData}
          loop={!reducedMotion}
          autoplay={!reducedMotion}
          rendererSettings={lottieRendererSettings}
          style={lottieContentStyle}
        /></Box>
        <Box sx={{ flex: 1, minWidth: 0, alignSelf: 'center' }}>
          <Typography id={titleId} component="h2" sx={{
            color: '#fff', fontWeight: 700, mb: description ? 0.5 : 0,
            textAlign: 'left', lineHeight: 1.25, letterSpacing: '-0.4px',
            fontSize: { xs: 22, sm: 24 }, overflowWrap: 'anywhere',
          }}>{title}</Typography>
          {description && <Typography id={descriptionId} component="div" sx={{
            color: '#B9BAC5', textAlign: 'left', lineHeight: '22px',
            fontSize: 14, fontWeight: 400, overflowWrap: 'anywhere',
          }}>{description}</Typography>}
        </Box>
        {showCloseButton && <IconButton aria-label={closeLabel} onClick={onClose} sx={{
          color: '#fff', width: 36, height: 36, flexShrink: 0,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
        }}><Close /></IconButton>}
      </Box>
      {children && <DialogContent sx={[
        { px: { xs: 3, sm: 3.5 }, py: 2.5, overflowX: 'hidden', textAlign: 'left' },
        ...(Array.isArray(contentSx) ? contentSx : contentSx ? [contentSx] : []),
      ]}>{children}</DialogContent>}
      {actions && <DialogActions sx={{
        px: { xs: 3, sm: 3.5 }, pt: 3, pb: 2.5, flexShrink: 0,
        borderTop: children ? '1px solid rgba(255,255,255,0.1)' : undefined,
        width: '100%', boxSizing: 'border-box',
      }}><ModalActionGroup>{actions}</ModalActionGroup></DialogActions>}
    </Dialog>
  );
}

export type ModalButtonTone = 'primary' | 'secondary';

/** A consistent action shape and focus treatment for every dialog. */
export function ModalButton<C extends React.ElementType = 'button'>({ tone = 'primary', sx, ...props }: Omit<ButtonProps<C>, 'color' | 'variant' | 'size'> & { component?: C; tone?: ModalButtonTone }) {
  const secondary = tone === 'secondary';
  const fill = '#D8A363';
  return <Button {...props} variant={secondary ? 'outlined' : 'contained'} sx={[
    {
      ...actionButtonSizingSx,
      borderRadius: '12px', boxShadow: 'none',
      color: secondary ? '#fff' : '#101219',
      bgcolor: secondary ? 'rgba(255,255,255,0.025)' : fill,
      border: secondary ? '1px solid rgba(255,255,255,0.22)' : '1px solid transparent',
      '&:hover': {
        bgcolor: secondary ? 'rgba(255,255,255,0.07)' : fill,
        borderColor: secondary ? 'rgba(255,255,255,0.38)' : 'transparent',
        filter: secondary ? undefined : 'brightness(1.08)',
        boxShadow: 'none',
      },
      '&.Mui-focusVisible': { outline: '2px solid #76A9E7', outlineOffset: 3 },
      '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.16)', color: '#B9BAC5', borderColor: 'transparent' },
    },
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ]} />;
}
